import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError, streamFetch } from "../lib/api";
import { readSse } from "../lib/sse";
import { logError } from "../lib/log";
import { useAuth } from "../lib/auth";
import { ArrowLeft, Sparkles, Download, WifiOff, Zap } from "lucide-react";
import { getOfflineLesson, saveOfflineLesson, removeOfflineLesson, useOnline } from "../lib/offline";
import { LessonMarkdown } from "../components/lesson/LessonMarkdown";
import { AutoQuiz } from "../components/lesson/AutoQuiz";
import { LessonActions, LessonExcerpts } from "../components/lesson/LessonParts";

const PLAYLIST_KEY = "chemlab_playlist";

/* Loads lesson meta/excerpts (or the saved offline copy) and streams the explainer. */
function useLesson(year, order, user) {
  const [lesson, setLesson] = useState(null);
  const [content, setContent] = useState("");
  const [status, setStatus] = useState("idle"); // idle | streaming | done | offline | error | missing
  const [excerpts, setExcerpts] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [saved, setSaved] = useState(() => !!getOfflineLesson(year, order));
  const [cachedAt, setCachedAt] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    setLesson(null); setContent(""); setStatus("idle"); setExcerpts([]); setCachedAt(null);
    const cached = getOfflineLesson(year, order);
    setSaved(!!cached);
    if (cached) {
      setLesson(cached.lesson); setExcerpts(cached.excerpts || []); setContent(cached.content); setStatus("offline");
      return;
    }
    api.get(`/curriculum/${year}/${order}`).then((r) => setLesson(r.data)).catch((e) => setStatus(e?.response?.status === 404 ? "missing" : "error"));
    api.get(`/curriculum/${year}/${order}/excerpts`).then((r) => setExcerpts(r.data.excerpts)).catch((e) => logError("Excerpts failed", e));
  }, [year, order]);

  useEffect(() => {
    setCompleted(false);
    if (!user) return;
    api.get("/curriculum/progress")
      .then((r) => setCompleted(!!r.data.by_year.find((b) => b.year === Number(year))?.read?.includes(Number(order))))
      .catch((e) => logError("Progress failed", e));
  }, [year, order, user]);

  const stream = useCallback(async (force = false) => {
    setContent("");
    setStatus("streaming");
    setCachedAt(null);
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const live = () => abortRef.current === ctrl && !ctrl.signal.aborted;
    try {
      const resp = await streamFetch(`/curriculum/${year}/${order}/generate${force === true ? "?force=true" : ""}`, { method: "POST", signal: ctrl.signal });
      if (!resp.ok || !resp.body) throw new Error(`Lesson stream failed (${resp.status})`);
      let acc = "";
      await readSse(resp, (chunk) => { acc += chunk; if (live()) setContent(acc); }, {
        onEvent: (name, data) => { if (name === "cached" && live()) setCachedAt(data?.generated_at || true); },
      });
      if (live()) setStatus("done");
    } catch (e) {
      if (!live()) return; // superseded or aborted — a newer stream owns the UI
      logError("Lesson stream failed", e);
      setStatus("error");
    }
  }, [year, order]);

  useEffect(() => { if (lesson && status === "idle") stream(); }, [lesson, status, stream]);
  useEffect(() => () => abortRef.current?.abort(), [year, order]);
  useEffect(() => {
    if (status === "done" && saved && lesson && content) saveOfflineLesson({ lesson, content, excerpts });
  }, [status, saved, lesson, content, excerpts]);

  return { lesson, content, status, excerpts, completed, setCompleted, saved, setSaved, stream, cachedAt };
}

export default function Lesson() {
  const { year, order } = useParams();
  const { user } = useAuth();
  const online = useOnline();
  const { lesson, content, status, excerpts, completed, setCompleted, saved, setSaved, stream, cachedAt } = useLesson(year, order, user);
  const regenerate = () => stream(true);
  const [added, setAdded] = useState(false);
  const [actionErr, setActionErr] = useState(null);

  const excerptByPage = useMemo(() => Object.fromEntries(excerpts.map((e) => [e.page, e.text])), [excerpts]);

  const run = async (fn, fallback) => {
    try { await fn(); } catch (e) { setActionErr(apiError(e, fallback)); }
  };
  const markComplete = () => user && !completed && run(async () => { await api.post("/curriculum/complete", { year: Number(year), order: Number(order) }); setCompleted(true); }, "Could not mark as read.");
  const toggleComplete = () => {
    if (!user) return;
    if (!completed) return markComplete();
    return run(async () => { await api.delete(`/curriculum/complete?year=${year}&order=${order}`); setCompleted(false); }, "Could not update progress.");
  };
  const toggleOffline = () => {
    try {
      if (saved) removeOfflineLesson(year, order); else saveOfflineLesson({ lesson, content, excerpts });
      setSaved(!saved);
    } catch (e) {
      logError("Offline save failed", e);
      setActionErr("Could not save offline (device storage is full or blocked).");
    }
  };
  const addToPlaylist = () => {
    try {
      const list = JSON.parse(localStorage.getItem(PLAYLIST_KEY) || "[]");
      list.push({ kind: "lesson", year: Number(year), order: Number(order), id: `lesson-${year}-${order}-${Date.now()}`, title: lesson?.title });
      localStorage.setItem(PLAYLIST_KEY, JSON.stringify(list));
      setAdded(true);
      setTimeout(() => setAdded(false), 1500);
    } catch (e) {
      logError("Playlist save failed", e);
      setActionErr("Could not save to playlist (storage unavailable).");
    }
  };

  if (status === "missing") return <div className="rounded-md border border-red-400/40 bg-red-500/10 p-4 text-sm text-red-200" data-testid="lesson-missing">Lesson not found.</div>;
  if (!lesson && status === "error" && !online) {
    return (
      <div className="rounded-md border border-amber-400/40 bg-amber-500/10 p-4 text-sm text-amber-100" data-testid="lesson-offline-unavailable">
        <div className="flex items-center gap-2 font-semibold"><WifiOff size={14} /> You're offline</div>
        <div className="mt-1 text-xs text-amber-200/80">This lesson isn't saved on your device. <Link to="/offline" className="underline">See saved lessons →</Link></div>
      </div>
    );
  }
  if (!lesson) return <div className="text-slate-400">Loading lesson…</div>;

  return (
    <div className="space-y-8" data-testid="lesson-root">
      <div>
        <Link to="/curriculum" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 hover:text-cyan-300" data-testid="back-to-curriculum"><ArrowLeft size={14} /> Curriculum</Link>
        <div className="mt-4 flex items-baseline gap-3">
          <span className="font-mono-num rounded-md border border-cyan-400/30 bg-cyan-500/10 px-2 py-0.5 text-xs text-cyan-300">Y{lesson.year} · {lesson.month} · #{String(lesson.order).padStart(2, "0")}</span>
          <span className="text-[10px] uppercase tracking-widest text-slate-500">{lesson.unit}</span>
        </div>
        <h1 className="font-display mt-3 text-3xl font-bold tracking-tight sm:text-4xl">{lesson.title}</h1>
        {status === "offline" && (
          <div className="mt-3 inline-flex items-center gap-2 rounded-md border border-lime-400/30 bg-lime-500/10 px-3 py-1 text-xs text-lime-200" data-testid="lesson-offline-badge">
            <Download size={12} /> Saved copy{online ? " · tap Regenerate for a fresh version" : " · you're offline"}
          </div>
        )}
        {status === "done" && cachedAt && (
          <div className="mt-3 inline-flex items-center gap-2 rounded-md border border-cyan-400/30 bg-cyan-500/10 px-3 py-1 text-xs text-cyan-200" data-testid="lesson-cached-badge">
            <Zap size={12} /> Instant load{typeof cachedAt === "string" ? ` · generated ${new Date(cachedAt).toLocaleDateString()}` : ""} · <button onClick={regenerate} className="underline" data-testid="lesson-cached-regenerate">regenerate</button> for a fresh version
          </div>
        )}
      </div>

      <article data-testid="lesson-content" className="rounded-md border border-white/10 bg-black/40 p-6 leading-relaxed text-slate-300">
        {content ? <LessonMarkdown content={content} excerptByPage={excerptByPage} /> : (
          <div className="flex items-center gap-2 text-sm text-slate-500"><Sparkles size={14} /> Generating explainer with ChemBuddy…</div>
        )}
        {status === "error" && (
          <div className="mt-4 rounded-md border border-red-400/40 bg-red-500/10 p-3 text-sm text-red-200">Failed to load lesson. <button onClick={() => stream()} className="underline">Retry</button></div>
        )}
      </article>

      <LessonActions year={year} order={order} status={status} online={online} saved={saved} completed={completed} added={added} user={user} actionErr={actionErr}
        onRegenerate={regenerate} onToggleOffline={toggleOffline} onToggleComplete={toggleComplete} onAddPlaylist={addToPlaylist} />

      {status === "done" && <AutoQuiz year={year} order={order} onPass={markComplete} />}
      <LessonExcerpts excerpts={excerpts} />
    </div>
  );
}
